
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl/dist';
import { useHistory } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronCircleDown, faChevronCircleUp } from '@fortawesome/free-solid-svg-icons';

import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import CurrentEnrollmentContainer from './CurrentEnrollmentsContainer';
import PastEnrollmentContainer from './PastEnrollmentsContainer';
import { getEnRollmentHistory } from '../../../actions/individualPortal/enrollmentHistory/enrollmentHistoryActionCreators';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { environmentSelector } from '../../../selectors/commonSelectors';
import { configs } from '../../../constants/configs/configs';
import { useStateCode } from '../../../hooks';


const StyledEnrollmentContainer = styled.div`
    margin-bottom: 15px;
`;

const StyledContainer = styled.div`
    margin: 20px 0px;
    display: flex ;
    justify-content: space-between ;
    background-color: ${({ theme }) => theme.gray11};
    border-radius: 8px 8px 0 0;
    min-height: 40px;
    .enroll-year-text{
        display:inline-block;
        padding:10px 10px 0 0;
    }
`;

const StyledHeading = styled.h4`
    padding: 10px 0 0 10px;
    margin-bottom: 20px;
`;



const MyEnrollmentHistoryContainer = ({ isSetLoading = true }) => {

    const history = useHistory();
    const intl = useIntl();
    const [isLoading, setIsLoading] = useState(isSetLoading);
    const { hideToggleOnNoEnrollment } = useStateCode();
    const { exchangeStartYear, coverageYear } = useAppSelector(environmentSelector);
    const dispatch = useAppDispatch();
    const enrollHistory = useAppSelector(state => state.individualPortal.enrollmentHistory);
    const [hideCurrentToggleVisible, setHideCurrentToggleVisible] = useState(false)
    const [hidePastToggleVisible, setHidePastToggleVisible] = useState(false);
    const [containerDisplayControl, setContainerDisplayControl] = useState({ 'isShowCurrentEnrollments': true, 'isShowPastEnrollments': true, selectYear: coverageYear });
    const enrolledYears: { label: string, value: string, selected?: boolean }[] = getEnrollmentYearOptions();

    useEffect(() => {
        if (enrollHistory.errorMessage) {
            history.push(configs.routes.HOME);
        }
        setHideCurrentToggleVisible(hideToggleOnNoEnrollment && enrollHistory.activeHealthEnrollments.length === 0 && enrollHistory.activeDentalEnrollments.length === 0);
        setHidePastToggleVisible(hideToggleOnNoEnrollment && enrollHistory.inactiveHealthEnrollments.length === 0 && enrollHistory.inactiveDentalEnrollments.length === 0);
    }, [enrollHistory]);

    useEffect(() => {
        const loadData = async () => {
            await dispatch(
                getEnRollmentHistory(containerDisplayControl.selectYear)
            );
            setIsLoading(false);
        };

        loadData();
    }, [containerDisplayControl.selectYear]);

    const handleCurrentEnrollmentToggle = () => {
        setContainerDisplayControl({ ...containerDisplayControl, 'isShowCurrentEnrollments': !containerDisplayControl.isShowCurrentEnrollments });
    };

    const handlePastEnrollmentToggle = () => {
        setContainerDisplayControl({ ...containerDisplayControl, 'isShowPastEnrollments': !containerDisplayControl.isShowPastEnrollments });
    };

    const handleSelectedYear = (e) => {
        setContainerDisplayControl({ ...containerDisplayControl, 'selectYear': e.target.value });
    };

    function getEnrollmentYearOptions() {
        const options: { label: string, value: string, selected?: boolean }[] = [];
        const allOptionLabel = intl.formatMessage({ id: 'indportal.enrollments.All' });
        options.push({ label: allOptionLabel, value: 'ALL'});
        let startYear = exchangeStartYear;
        let endYear = coverageYear;
        for (endYear; startYear <= endYear; endYear--) {
            const selectValue = endYear === coverageYear ? true : false;
            options.push({
                label: endYear, value: endYear , selected: selectValue 
            });
        }
        return options;
    };

    if (isLoading) {
        return null;
    }

    return (
        <>
            <StyledEnrollmentContainer className="my-enrollment-container">
                <StyledContainer className="header">
                    <StyledHeading className='heading' data-testid="current-enrollmentHeader">
                        <FormattedMessage id="indportal.enrollments.currentEnrollments" />
                    </StyledHeading>
                    <div>
                        <div className='enroll-year-text' data-testid="enrolled-year">
                            <FormattedMessage id="indportal.enrollments.enrollmentYear" />
                        </div>
                        <span>
                            <select onChange={handleSelectedYear} data-testid="activeEhCoverageYear" className='input-small'>
                                {
                                    enrolledYears.map(options => {
                                        return <option label={options.label} value={options.value} selected={options.selected || false} key={options.value}>
                                        </option>;
                                    })
                                }
                            </select>
                            <ConditionalRendering isTrue={!hideCurrentToggleVisible}>
                                <FontAwesomeIcon data-testid="current-toggle" className='margin-t-10 margin-r-10 margin-l-10' icon={containerDisplayControl.isShowCurrentEnrollments ? faChevronCircleUp : faChevronCircleDown} onClick={handleCurrentEnrollmentToggle} />
                            </ConditionalRendering>
                        </span>
                    </div>
                </StyledContainer>
                <ConditionalRendering isTrue={containerDisplayControl.isShowCurrentEnrollments}>
                    <CurrentEnrollmentContainer activeHealthEnrollments={enrollHistory.activeHealthEnrollments} activeDentalEnrollments={enrollHistory.activeDentalEnrollments} coverageStartDateForDisenrollment={enrollHistory.coverageStartDateForDisenrollment} coverageYear={containerDisplayControl.selectYear} />
                </ConditionalRendering>
            </StyledEnrollmentContainer>
            <StyledEnrollmentContainer className="my-enrollment-container">
                <StyledContainer className="header">
                    <StyledHeading className='heading' data-testid="enrollmentHeader">
                        <FormattedMessage id="indportal.enrollments.pastEnrollments" />
                    </StyledHeading>
                    <div>
                        <h4 className='enroll-year-text' data-testid="enrollmentHeaderCoverageYear" ><FormattedMessage id="indportal.enrollments.enrollmentYear" /> {containerDisplayControl.selectYear}</h4>
                        <ConditionalRendering isTrue={!hidePastToggleVisible}>
                            <FontAwesomeIcon data-testid="past-toggle" className='margin-t-10 margin-r-10' icon={containerDisplayControl.isShowPastEnrollments ? faChevronCircleUp : faChevronCircleDown} onClick={handlePastEnrollmentToggle} />
                        </ConditionalRendering>
                    </div>
                </StyledContainer>
                <ConditionalRendering isTrue={containerDisplayControl.isShowPastEnrollments}>
                    <PastEnrollmentContainer inactiveHealthEnrollments={enrollHistory.inactiveHealthEnrollments} inactiveDentalEnrollments={enrollHistory.inactiveDentalEnrollments} coverageStartDateForDisenrollment={enrollHistory.coverageStartDateForDisenrollment} coverageYear={containerDisplayControl.selectYear} />
                </ConditionalRendering>
            </StyledEnrollmentContainer>
        </>
    );
};

export default MyEnrollmentHistoryContainer;